import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { useCasaFacilProduct } from '../lib/casaFacilProduct';
import { 
  Search, 
  FileText, 
  User, 
  Car, 
  Calendar,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

function MaxparPolicyQuery() {
  const { user } = useAuth();
  const product = useCasaFacilProduct();
  const [apiConfigs, setApiConfigs] = useState([]);
  const [selectedApiConfig, setSelectedApiConfig] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [policyData, setPolicyData] = useState(null);
  const [error, setError] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  
  // Estados para filtro por período
  const [showPeriodFilter, setShowPeriodFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [policiesList, setPoliciesList] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);

  console.log('🔄 Componente MaxparPolicyQuery renderizando...', { user });

  // Carregar configurações de API da MAXPAR
  useEffect(() => {
    loadMaxparApiConfigs();
  }, []);

  const loadMaxparApiConfigs = async () => {
    try {
      console.log('⚙️ Carregando configurações de API da MAXPAR...');
      const response = await api.get('/api-configs/active/list');
      
      // Filtrar apenas configurações da MAXPAR
      const maxparConfigs = response.data.configs?.filter(config => 
        config.api_owner.toLowerCase() === 'maxpar'
      ) || [];
      
      setApiConfigs(maxparConfigs);
      console.log('✅ Configurações da MAXPAR carregadas:', maxparConfigs);
      
      // Selecionar primeira configuração por padrão
      if (maxparConfigs.length > 0) {
        setSelectedApiConfig(maxparConfigs[0].id);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar configurações da MAXPAR:', error);
      toast.error('Erro ao carregar configurações da API');
    }
  };

  const handleSearch = async () => {
    if (!policyNumber.trim()) {
      toast.error('Digite o número da apólice');
      return;
    }

    if (!selectedApiConfig) {
      toast.error('Selecione uma configuração de API');
      return;
    }

    setLoading(true);
    setError(null);
    setPolicyData(null);

    try {
      console.log(`🔍 Consultando apólice ${policyNumber} na MAXPAR...`);
      
      const response = await api.get(`/upload/maxpar/policy/${encodeURIComponent(policyNumber)}`, {
        params: { api_config_id: selectedApiConfig }
      });

      if (response.data.success) {
        // A API retorna um array, pegar o primeiro elemento
        const policyResult = response.data.data[0];
        setPolicyData(policyResult);
        
        // Adicionar à história de consultas com os dados
        const newSearch = {
          id: Date.now(),
          policyNumber,
          timestamp: new Date().toISOString(),
          success: true,
          result: policyResult,
          apiConfigId: selectedApiConfig
        };
        
        console.log('📝 Adicionando ao histórico:', {
          id: newSearch.id,
          policyNumber: newSearch.policyNumber,
          success: newSearch.success,
          hasResult: !!newSearch.result,
          resultKeys: newSearch.result ? Object.keys(newSearch.result) : null
        });
        
        setSearchHistory(prev => [newSearch, ...prev.slice(0, 9)]); // Manter apenas 10 últimas
        
        toast.success('Apólice encontrada com sucesso!');
      } else {
        setError('Apólice não encontrada');
        toast.error('Apólice não encontrada');
      }
    } catch (error) {
      console.error('❌ Erro ao consultar apólice:', error);
      
      const errorMessage = error.response?.data?.error || 'Erro ao consultar apólice';
      setError(errorMessage);
      toast.error(errorMessage);
      
      // Adicionar à história de consultas
      const newSearch = {
        id: Date.now(),
        policyNumber,
        timestamp: new Date().toISOString(),
        success: false,
        error: errorMessage
      };
      setSearchHistory(prev => [newSearch, ...prev.slice(0, 9)]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setPolicyNumber('');
    setPolicyData(null);
    setError(null);
  };

  const viewSearchResult = (search) => {
    if (search.success && search.result) {
      setSelectedResult(search.result);
      setShowResultModal(true);
    }
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    toast.success('Histórico limpo');
  };

  const handlePeriodFilter = async () => {
    if (!startDate || !endDate) {
      toast.error('Selecione as datas de início e fim');
      return;
    }

    if (!selectedApiConfig) {
      toast.error('Selecione uma configuração de API');
      return;
    }

    setLoadingPolicies(true);
    setError(null);

    try {
      console.log(`🔍 Buscando apólices do período ${startDate} a ${endDate}...`);
      
      const response = await api.get('/upload/maxpar/policies', {
        params: { 
          start_date: startDate,
          end_date: endDate,
          api_config_id: selectedApiConfig
        }
      }); 

      if (response.data.success) {
        setPoliciesList(response.data.policies);
        toast.success(`${response.data.total} apólices encontradas no período`);
      } else {
        setError('Erro ao buscar apólices');
        toast.error('Erro ao buscar apólices');
      }
    } catch (error) {
      console.error('❌ Erro ao buscar apólices:', error);
      
      const errorMessage = error.response?.data?.error || 'Erro ao buscar apólices';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const clearPeriodFilter = () => {
    setStartDate('');
    setEndDate('');
    setPoliciesList([]);
    setError(null);
  };

  const viewPolicyDetails = (policyNumber) => {
    setPolicyNumber(policyNumber);
    // Simular clique no botão de busca
    setTimeout(() => {
      handleSearch();
    }, 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    } catch {
      return value;
    }
  };

  const renderPolicyDetails = () => {
    if (!policyData) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary-600" />
            Detalhes da Apólice
          </h3>
          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            Ativa
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Informações Básicas */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-gray-500" />
              Informações Básicas
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500">Número:</span>
                <p className="font-medium">{policyData.Apolice || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Parceiro:</span>
                <p className="font-medium">{policyData.Parceiro || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Produto:</span>
                <p className="font-medium">{policyData.Produto || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Início Vigência:</span>
                <p className="font-medium">{formatDate(policyData.DataInicioVigencia)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Fim Vigência:</span>
                <p className="font-medium">{formatDate(policyData.DataFimVigencia)}</p>
              </div>
            </div>
          </div>

          {/* Dados do Segurado */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center">
              <User className="h-4 w-4 mr-2 text-gray-500" />
              Segurado
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500">Nome:</span>
                <p className="font-medium">{policyData.Segurado?.Nome || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">CPF/CNPJ:</span>
                <p className="font-medium">{policyData.Segurado?.Documento || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Email:</span>
                <p className="font-medium flex items-center">
                  <Mail className="h-3 w-3 mr-1" />
                  {policyData.Segurado?.Email || '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Data Nascimento:</span>
                <p className="font-medium">{formatDate(policyData.Segurado?.DataNascimento)}</p>
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-700 flex items-center">
              <DollarSign className="h-4 w-4 mr-2 text-gray-500" />
              Valores
            </h4>
            <div className="space-y-2">
              <div>
                <span className="text-sm text-gray-500">Prêmio:</span>
                <p className="font-medium text-green-600">
                  {formatCurrency(policyData.valor_premio || policyData.valorPremio)}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">IOF:</span>
                <p className="font-medium">{formatCurrency(policyData.valor_iof || policyData.valorIof)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Total:</span>
                <p className="font-medium text-primary-600 font-semibold">
                  {formatCurrency(policyData.valor_total || policyData.valorTotal)}
                </p>
              </div>
            </div>
          </div>

          {/* Dados do Veículo (se aplicável) */}
          {policyData.veiculo && (
            <div className="space-y-3 md:col-span-2 lg:col-span-3">
              <h4 className="font-medium text-gray-700 flex items-center">
                <Car className="h-4 w-4 mr-2 text-gray-500" />
                Veículo
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Placa:</span>
                  <p className="font-medium">{policyData.veiculo.placa || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Marca/Modelo:</span>
                  <p className="font-medium">{policyData.veiculo.marca} {policyData.veiculo.modelo}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Ano:</span>
                  <p className="font-medium">{policyData.veiculo.ano_modelo || policyData.veiculo.anoFabricacao || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Chassi:</span>
                  <p className="font-medium text-xs">{policyData.veiculo.chassi || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Endereço */}
          {policyData.Segurado?.Endereco && (
            <div className="space-y-3 md:col-span-2 lg:col-span-3">
              <h4 className="font-medium text-gray-700 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                Endereço
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <span className="text-sm text-gray-500">Logradouro:</span>
                  <p className="font-medium">
                    {policyData.Segurado.Endereco.Logradouro} {policyData.Segurado.Endereco.Numero}
                    {policyData.Segurado.Endereco.Complemento && `, ${policyData.Segurado.Endereco.Complemento}`}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Bairro:</span>
                  <p className="font-medium">{policyData.Segurado.Endereco.Bairro}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Cidade/Estado:</span>
                  <p className="font-medium">
                    {policyData.Segurado.Endereco.Cidade} - {policyData.Segurado.Endereco.Estado}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">CEP:</span>
                  <p className="font-medium">{policyData.Segurado.Endereco.Cep}</p>
                </div>
              </div>
            </div>
          )}

          {/* Planos */}
          {policyData.Planos && policyData.Planos.length > 0 && (
            <div className="space-y-3 md:col-span-2 lg:col-span-3">
              <h4 className="font-medium text-gray-700 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-gray-500" />
                Planos
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {policyData.Planos.map((plano, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-500">Código:</span>
                        <p className="font-medium">{plano.Codigo}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Início Vigência:</span>
                        <p className="font-medium">{formatDate(plano.DataInicioVigencia)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500">Fim Vigência:</span>
                        <p className="font-medium">{formatDate(plano.DataFimVigencia)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPoliciesList = () => {
    if (policiesList.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-primary-600" />
            Apólices Integradas ({policiesList.length})
          </h3>
          <button
            onClick={clearPeriodFilter}
            className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
          >
            Limpar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Apólice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vigência
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Integração
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {policiesList.map((policy) => (
                <tr key={policy.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {policy.policy_number}
                    </div>
                    <div className="text-sm text-gray-500">
                      {policy.partner}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {policy.customer_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {policy.customer_document}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {policy.product}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(policy.start_date)} - {formatDate(policy.end_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(policy.integration_date)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {policy.file_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => viewPolicyDetails(policy.policy_number)}
                      className="text-primary-600 hover:text-primary-900 mr-3"
                    >
                      Ver Detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSearchHistory = () => {
    if (searchHistory.length === 0) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Histórico de Consultas</h3>
          {searchHistory.length > 0 && (
            <button
              onClick={clearSearchHistory}
              className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200 transition-colors"
            >
              Limpar Histórico
            </button>
          )}
        </div>
        <div className="space-y-2">
          {searchHistory.map((search) => (
            <div key={search.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                {search.success ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-red-500" />
                )}
                <span className="font-medium">{search.policyNumber}</span>
                <span className="text-sm text-gray-500">
                  {new Date(search.timestamp).toLocaleString('pt-BR')}
                </span>
                {/* Debug info */}
                <span className="text-xs text-gray-400">
                  {search.success ? '✅' : '❌'} {search.result ? '📄' : '📭'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {!search.success && (
                  <span className="text-sm text-red-600">{search.error}</span>
                )}
                {search.success && search.result && (
                  <button
                    onClick={() => viewSearchResult(search)}
                    className="px-3 py-1 bg-primary-100 text-primary-700 rounded-md text-sm hover:bg-primary-200 transition-colors"
                  >
                    Ver Resultado
                  </button>
                )}
                {/* Debug info */}
                {search.success && !search.result && (
                  <span className="text-xs text-orange-600">Sem resultado</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout
      headerTitle={product?.label || 'CASA FÁCIL RESOLVE'}
      headerSubtitle="Consulta de apólices MAXPAR"
    >
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {product?.label || 'CASA FÁCIL RESOLVE'} · Consulta MAXPAR
          </h1>
          <p className="text-gray-600">
            Consulte informações detalhadas de apólices no sistema MAXPAR
          </p>
        </div>

        {/* Formulário de Consulta */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {showPeriodFilter ? 'Filtrar Apólices por Período' : 'Consultar Apólice'}
            </h2>
            <button
              onClick={() => {
                setShowPeriodFilter(!showPeriodFilter);
                clearSearch();
                clearPeriodFilter();
              }}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-800 border border-primary-300 rounded-md hover:bg-primary-50"
            >
              {showPeriodFilter ? 'Busca Individual' : 'Filtrar por Período'}
            </button>
          </div>
          
          {showPeriodFilter ? (
            /* Formulário de Filtro por Período */
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              {/* Configuração da API */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Configuração da API
                </label>
                <select
                  value={selectedApiConfig}
                  onChange={(e) => setSelectedApiConfig(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Selecione uma configuração</option>
                  {apiConfigs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.api_function}
                    </option>
                  ))}
                </select>
              </div>

              {/* Data de Início */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Início
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Data de Fim */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Fim
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Botões de Ação */}
              <div className="flex items-end space-x-2">
                <button
                  onClick={handlePeriodFilter}
                  disabled={loadingPolicies}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingPolicies ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Buscando...
                    </div>
                  ) : (
                    'Buscar Apólices'
                  )}
                </button>
                <button
                  onClick={clearPeriodFilter}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Limpar
                </button>
              </div>
            </div>
          ) : (
            /* Formulário de Busca Individual */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Configuração da API */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Configuração da API
                </label>
                <select
                  value={selectedApiConfig}
                  onChange={(e) => setSelectedApiConfig(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Selecione uma configuração</option>
                  {apiConfigs.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.api_function}
                    </option>
                  ))}
                </select>
              </div>

              {/* Número da Apólice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                Número da Apólice
              </label>
              <input
                type="text"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                placeholder="Digite o número da apólice"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Botão de Consulta */}
            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={loading || !policyNumber.trim() || !selectedApiConfig}
                className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <Clock className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Consultando...' : 'Consultar'}
              </button>
            </div>
          </div>
          )}

          {/* Botões de Ação */}
          {!showPeriodFilter && (
            <div className="flex space-x-3">
              <button
                onClick={clearSearch}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Limpar
              </button>
            </div>
          )}
        </div>

        {/* Mensagem de Erro */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Resultados da Consulta */}
        {renderPolicyDetails()}

        {/* Lista de Apólices por Período */}
        {renderPoliciesList()}

        {/* Histórico de Consultas */}
        {renderSearchHistory()}
      </div>

      {/* Modal de Resultado */}
      {showResultModal && selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Resultado da Consulta - {selectedResult.Apolice}
              </h3>
              <button
                onClick={() => setShowResultModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Informações Básicas */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-gray-500" />
                    Informações Básicas
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500">Número:</span>
                      <p className="font-medium">{selectedResult.Apolice || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Parceiro:</span>
                      <p className="font-medium">{selectedResult.Parceiro || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Produto:</span>
                      <p className="font-medium">{selectedResult.Produto || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Início Vigência:</span>
                      <p className="font-medium">{formatDate(selectedResult.DataInicioVigencia)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Fim Vigência:</span>
                      <p className="font-medium">{formatDate(selectedResult.DataFimVigencia)}</p>
                    </div>
                  </div>
                </div>

                {/* Dados do Segurado */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 flex items-center">
                    <User className="h-4 w-4 mr-2 text-gray-500" />
                    Segurado
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500">Nome:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Nome || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">CPF/CNPJ:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Documento || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Email:</span>
                      <p className="font-medium flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {selectedResult.Segurado?.Email || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Data Nascimento:</span>
                      <p className="font-medium">{formatDate(selectedResult.Segurado?.DataNascimento)}</p>
                    </div>
                  </div>
                </div>

                {/* Endereço */}
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-700 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                    Endereço
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-500">Logradouro:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Logradouro || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Número:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Numero || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Bairro:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Bairro || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Cidade:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Cidade || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Estado:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Estado || '-'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">CEP:</span>
                      <p className="font-medium">{selectedResult.Segurado?.Endereco?.Cep || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Planos */}
              {selectedResult.Planos && selectedResult.Planos.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-gray-500" />
                    Planos
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedResult.Planos.map((plano, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm text-gray-500">Código:</span>
                            <p className="font-medium">{plano.Codigo || '-'}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Início Vigência:</span>
                            <p className="font-medium">{formatDate(plano.DataInicioVigencia)}</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Fim Vigência:</span>
                            <p className="font-medium">{formatDate(plano.DataFimVigencia)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Telefones */}
              {selectedResult.Telefones && selectedResult.Telefones.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3 flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-500" />
                    Telefones
                  </h4>
                  <div className="space-y-2">
                    {selectedResult.Telefones.map((telefone, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded-lg">
                        <span className="font-medium">
                          ({telefone.Ddd}) {telefone.Numero}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-6 border-t flex-shrink-0 bg-gray-50">
              <button
                onClick={() => setShowResultModal(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default MaxparPolicyQuery;
